import { Type } from "@sinclair/typebox";
import SchemaValidator from "../schema-validator";
import { proctoringOptionProfile } from "../proctoring-profile/schema-validators";
import { OptionalOrNull } from "../shared-schemas";

const instituteInfo = new SchemaValidator(
	Type.Object({
		id: Type.Integer(),
		uuid: Type.String(),
		name: Type.String(),
		origin: Type.String(),
	}),
);

const instituteLanguage = new SchemaValidator(
	Type.Object({
		language: Type.String(),
	}),
);

const verityInstituteSettings = new SchemaValidator(
	Type.Object({
		allowLiveProctoring: Type.Boolean(),
		liveProctoringResetableCredits: OptionalOrNull(Type.Number()),
		liveProctoringCreditsResetDate: OptionalOrNull(Type.String()),
		liveProctoringOneTimeCredits: Type.Number(),
		allowLivePopIn: Type.Boolean(),
		livePopInResetableCredits: OptionalOrNull(Type.Number()),
		livePopInCreditsResetDate: OptionalOrNull(Type.String()),
		livePopInOneTimeCredits: Type.Number(),
		allowLivePopInPostReview: Type.Boolean(),
		allowStandaloneMode: Type.Boolean(),
		allowAiStrictMode: Type.Boolean(),
		allowDemoMode: Type.Boolean(),
		allowExtensionPlatform: Type.Boolean(),
		allowLockdownPlatform: Type.Boolean(),
		allowMobilePlatform: Type.Boolean(),
		showAllAssessments: Type.Boolean(),
		liteMode: Type.Boolean(),
	}),
);

const integrated = new SchemaValidator(
	Type.Object({
		integrated: Type.Boolean(),
	}),
);

const CourseSettings = new SchemaValidator(
	Type.Object({
		"CourseSetting-allowStopRecording": Type.Boolean(),
		"InstituteSetting-allowStopRecording": Type.Boolean(),
		"CourseEmailSetting-courseEmailSetting": Type.Boolean(),
		"InstituteEmailSetting-instituteEmailSetting": Type.Boolean(),
		"Info-instituteEmailCount": Type.String(),
	}),
);

const instituteDisabledProctoringOptions = new SchemaValidator(Type.Array(Type.String()));

const instituteAllowStrictMode = new SchemaValidator(
	Type.Object({
		allowStrictMode: Type.Boolean(),
		allowAiStrictMode: Type.Boolean(),
	}),
);

const lmsAccounts = new SchemaValidator(
	Type.Array(
		Type.Object({
			id: Type.Integer(),
			accountName: Type.String(),
			lmsAccountId: Type.String(),
			parentLmsAccountId: OptionalOrNull(Type.String()),
			rootLmsAccountId: OptionalOrNull(Type.String()),
			lmsAccountState: OptionalOrNull(Type.String()),
			verityEnabled: Type.Boolean(),
		}),
	),
);

const instituteScopeMode = new SchemaValidator(
	Type.Object({
		scopeMode: Type.Union([
			Type.Literal("allCourses"),
			Type.Literal("selectedAccounts"),
			Type.Literal("selectedCourses"),
		]),
		allAccounts: Type.Array(
			Type.Object({
				id: Type.Integer(),
				accountName: Type.String(),
				lmsAccountId: Type.String(),
				parentLmsAccountId: OptionalOrNull(Type.String()),
				verityEnabled: Type.Boolean(),
			}),
		),
		selectedCourses: Type.Array(
			Type.Object({
				id: Type.Integer(),
				courseId: Type.String(),
				courseName: Type.String(),
				courseUrl: Type.String(),
			}),
		),
	}),
);

const instituteSuffix = new SchemaValidator(
	Type.Object({
		suffix: Type.Union([Type.String(), Type.Null()]),
	}),
);

const proctoringOptionKey = new SchemaValidator(Type.KeyOf(proctoringOptionProfile.properties.proctoringOption));

const instituteId = new SchemaValidator(
	Type.Object({
		id: Type.Number(),
	}),
);

const accessControlInstitute = new SchemaValidator(
	Type.Object({
		id: Type.String(),
		name: OptionalOrNull(Type.String()),
		origin: OptionalOrNull(Type.String()),
		platform: OptionalOrNull(Type.String()),
		hostnames: Type.Array(Type.String()),
	}),
);

const apiKey = new SchemaValidator(
	Type.Object({
		id: Type.Number(),
		keyId: OptionalOrNull(Type.String()),
		platform: OptionalOrNull(Type.String()),
		origin: OptionalOrNull(Type.String()),
		apiUrl: OptionalOrNull(Type.String()),
	}),
);

const thirdPartyPlatformRoster = new SchemaValidator(
	Type.Object({
		selectedPlatforms: Type.Array(
			Type.Object({
				platformId: Type.Number(),
				platformName: Type.String(),
			}),
		),
		unselectedPlatforms: Type.Array(
			Type.Object({
				platformId: Type.Number(),
				platformName: Type.String(),
			}),
		),
	}),
);

const verityEnabled = new SchemaValidator(
	Type.Object({
		verityEnabled: Type.Boolean(),
	}),
);

const apiKeys = new SchemaValidator(Type.Object({ apiKeys: Type.Array(apiKey.schema) }));

const hostnames = new SchemaValidator(Type.Object({ hostnames: Type.Array(Type.String()) }));

const thirdPartyIntegrations = new SchemaValidator(thirdPartyPlatformRoster.schema);

const instituteProctoringAccommodation = new SchemaValidator(
	Type.Array(
		Type.Object({
			userId: Type.Integer(),
			accommodationNotes: OptionalOrNull(Type.String()),
			proctoringOptionProfileId: OptionalOrNull(Type.Integer()),
		}),
	),
);

const ruleType = Type.Intersect([
	Type.Union([
		Type.Object({
			scope: Type.Literal("global"),
		}),
		Type.Object({
			scope: Type.Literal("lmsProvidedCourseId"),
			scopeValue: Type.String(),
		}),
		Type.Object({
			scope: Type.Literal("courseNamePrefix"),
			scopeValue: Type.String(),
		}),
	]),
	Type.Object({
		type: Type.Literal("editConfiguration"),
		details: OptionalOrNull(Type.Unknown()),
		enabled: Type.Boolean(),
	}),
]);

const rulesResponse = new SchemaValidator(Type.Array(ruleType));

const usageHours = new SchemaValidator(Type.Object({ hours: Type.Number() }));

const instituteSettings = new SchemaValidator(
	Type.Object({
		suffix: OptionalOrNull(Type.String()),
		managedGuestSessionRequireIncognito: Type.Boolean(),
	}),
);

export default {
	instituteInfo,
	verityInstituteSettings,
	integrated,
	CourseSettings,
	instituteLanguage,
	instituteDisabledProctoringOptions,
	instituteAllowStrictMode,
	instituteScopeMode,
	lmsAccounts,
	instituteSuffix,
	proctoringOptionKey,
	instituteId,
	accessControlInstitute,
	apiKey,
	apiKeys,
	verityEnabled,
	hostnames,
	thirdPartyIntegrations,
	instituteProctoringAccommodation,
	rulesResponse,
	usageHours,
	instituteSettings,
};
